import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CopyPlusIcon, FileIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Center,
  ConfirmationDialog,
  EmptyDataDash,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Row,
  StickyTable,
  TableCellDateTimePicker,
  TableCellEnumSelect,
  TableCellTextInput,
} from "@/components";
import { InterviewDialog } from "@jobs/candidate-sheet-v2/status-change-dialogs/InterviewDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useDuplicateAddInterview } from "@/hooks/createAdds/useDuplicateAddInterview";
import { useDeleteRemoveInterview } from "@/hooks/deleteRemoves/useDeleteRemoveInterview";
import { useGetInterviews } from "@/hooks/queries/useGetInterviews";
import { useUpdateSetInterview } from "@/hooks/updateSets/useUpdateSetInterview";
import { cn } from "@/lib/utils";
import {
  interviewDurationOptions,
  interviewStatusOptions,
  interviewStatusToBackgroundColor,
  interviewTypeKindClasses,
  timezoneOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  Interview,
  InterviewDialogMode,
  InterviewDuration,
  InterviewQueryParams,
  InterviewStatus,
  InterviewType,
  Job,
  Timezone,
} from "@/types";

const interviewFormSchema = z.object({
  id: z.number(),
  candidacyId: z.string().nullable(),
  title: z.string().min(1, "Title is required"),
  description: z.string().nullable(),
  dateTime: z.string().min(1, "Date and time are required"),
  durationMinutes: z.nativeEnum(InterviewDuration).nullable(),
  status: z.nativeEnum(InterviewStatus).nullable(),
  timezone: z.nativeEnum(Timezone).nullable(),
});

const schema = z.object({
  interviews: z.array(interviewFormSchema),
});

const toInterviewDuration = (
  durationMinutes: number | null,
): InterviewDuration | null => {
  if (durationMinutes === null) return null;
  const duration = durationMinutes.toString();
  return Object.values(InterviewDuration).includes(
    duration as InterviewDuration,
  )
    ? (duration as InterviewDuration)
    : null;
};

const toTimezone = (timezone: string | null): Timezone | null => {
  if (!timezone) return null;
  return Object.values(Timezone).includes(timezone as Timezone)
    ? (timezone as Timezone)
    : null;
};

const interviewToSchema = (interview: Interview) => ({
  ...interview,
  dateTime: interview.timezone
    ? dates.isoToDatetimeLocalInTimezone(interview.dateTime, interview.timezone)
    : dates.isoToDatetimeLocal(interview.dateTime),
  description: interview.description ?? null,
  durationMinutes: toInterviewDuration(interview.durationMinutes),
  status: interview.status ?? null,
  timezone: toTimezone(interview.timezone),
});

export const JobInterviewsTable = ({
  job,
  interviewQueryParams,
}: {
  job: Job;
  interviewQueryParams: InterviewQueryParams;
}) => {
  const currentCompanyId = useCurrentCompanyId();

  /* UseStates */
  const [isAddInterviewDialogOpen, setIsAddInterviewDialogOpen] =
    useState(false);
  const [isAddScreenDialogOpen, setIsAddScreenDialogOpen] = useState(false);
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState<z.infer<
    typeof interviewFormSchema
  > | null>(null);

  /* Database CRUD Operations */
  // Create
  const { mutate: duplicateAddInterview } = useDuplicateAddInterview({
    companyId: currentCompanyId,
    jobId: job.id,
    queryParams: interviewQueryParams,
  });

  // Read
  const {
    data: interviews,
    isPending: isInterviewsPending,
    isError: isInterviewsError,
  } = useGetInterviews({
    jobId: job.id,
    queryOptions: interviewQueryParams,
  });
  // Update
  const { mutate: updateSetInterview } = useUpdateSetInterview({
    companyId: currentCompanyId,
    jobId: job.id,
    queryParams: interviewQueryParams,
  });

  // Delete
  const { mutate: deleteRemoveInterview } = useDeleteRemoveInterview({
    companyId: currentCompanyId,
    jobId: job.id,
    queryParams: interviewQueryParams,
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { interviews: [] },
  });
  const { fields } = useFieldArray({
    control: form.control,
    name: "interviews",
    keyName: "interviewFormId",
  });

  const handleAddInterview = () => {
    mixpanelUtils.trackButtonClick("Add Interview", MIXPANEL_SCREENS.JOB);
    setIsAddInterviewDialogOpen(true);
  };

  const handleAddScreen = () => {
    mixpanelUtils.trackButtonClick("Add Screen", MIXPANEL_SCREENS.JOB);
    setIsAddScreenDialogOpen(true);
  };

  // Populate form with interviews data. Re-seed when rows are added/removed
  // (id set changes), or when the server data changes while the user isn't
  // mid-edit — so external updates (e.g. a reschedule from the "screening in progress modal")
  // surface in the cells without clobbering in-progress edits.
  useEffect(() => {
    if (!interviews) return;
    const serverIds = interviews.map((i) => i.id).join(",");
    const formIds = form
      .getValues("interviews")
      .map((i) => i.id)
      .join(",");
    const idsChanged = serverIds !== formIds;
    if (idsChanged || !form.formState.isDirty) {
      form.reset({
        interviews: interviews.map((i) => interviewToSchema(i)),
      });
    }
  }, [form, interviews]);

  const handleOnBlur = (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => {
    const formInterview = form.getValues("interviews").find((i) => i.id === id);

    // For dateTime edits, use the interview's timezone for conversion.
    if (fieldName === "dateTime" && updatedValue) {
      const timezone = formInterview?.timezone;
      const isoValue = timezone
        ? dates.datetimeLocalToIsoInTimezone(updatedValue, timezone)
        : dates.datetimeLocalToIso(updatedValue);
      if (!isoValue) return;
      updateSetInterview({
        id,
        updateInterviewRequest: { dateTime: isoValue },
      });
      return;
    }

    // When timezone changes, recalculate the UTC dateTime from the current
    // wall-clock time (displayed in the form) using the new timezone.
    if (
      fieldName === "timezone" &&
      updatedValue &&
      Object.values(Timezone).includes(updatedValue as Timezone)
    ) {
      const timezone = updatedValue as Timezone;
      const currentDatetimeLocal = formInterview?.dateTime;
      if (currentDatetimeLocal) {
        const newIso = dates.datetimeLocalToIsoInTimezone(
          currentDatetimeLocal,
          timezone,
        );
        if (!newIso) return;
        updateSetInterview({
          id,
          updateInterviewRequest: {
            timezone,
            dateTime: newIso,
          },
        });
        return;
      }
    }

    const valueToUpdate = misc.handleFormFieldToDatabaseValue(
      updatedValue,
      type,
    );
    updateSetInterview({
      id,
      updateInterviewRequest: { [fieldName]: valueToUpdate },
    });
  };

  const handleDeleteInterview = (index: number) => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const interview = form.getValues(`interviews.${index}`);
    setInterviewToDelete(interview);
    setIsDeleteConfirmationDialogOpen(true);
  };

  const handleDeleteInterviewConfirmation = () => {
    if (!interviewToDelete) return;
    mixpanelUtils.trackButtonClick("Delete Interview", MIXPANEL_SCREENS.JOB);
    const idToDelete = interviewToDelete.id;
    deleteRemoveInterview(
      { id: idToDelete },
      {
        onSuccess: () => {
          setIsDeleteConfirmationDialogOpen(false);
          setInterviewToDelete(null);
          form.setValue(
            "interviews",
            form.getValues("interviews").filter((i) => i.id !== idToDelete),
          );
        },
      },
    );
  };

  const handleDuplicateInterview = (index: number) => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const interview = form.getValues(`interviews.${index}`);

    mixpanelUtils.trackButtonClick("Duplicate Interview", MIXPANEL_SCREENS.JOB);
    duplicateAddInterview(
      { id: interview.id },
      {
        onSuccess: (duplicatedInterview) => {
          form.setValue("interviews", [
            ...form.getValues("interviews"),
            interviewToSchema(duplicatedInterview),
          ]);
        },
      },
    );
  };

  const listName = "interviews";

  const AddScreenButton = () => (
    <Button
      className="rounded-full"
      type="button"
      variant="outline"
      onClick={handleAddScreen}
    >
      <PlusIcon />
      Add Screen
    </Button>
  );

  const renderDialogs = () => (
    <>
      <InterviewDialog
        onOpenChange={setIsAddInterviewDialogOpen}
        open={isAddInterviewDialogOpen}
      />
      <InterviewDialog
        mode={InterviewDialogMode.SCREEN}
        onOpenChange={setIsAddScreenDialogOpen}
        open={isAddScreenDialogOpen}
      />
      <ConfirmationDialog
        confirmationButtonVariant="destructive"
        confirmButtonText="Delete"
        description={`Are you sure you want to delete interview "${interviewToDelete?.title ?? ""}"? This action cannot be undone.`}
        onConfirm={handleDeleteInterviewConfirmation}
        onOpenChange={setIsDeleteConfirmationDialogOpen}
        open={isDeleteConfirmationDialogOpen}
        title="Delete Interview"
      />
    </>
  );

  const render = () => {
    if (isInterviewsPending) {
      return (
        <Center className="h-full">
          <LoadingIndicator variant="xlarge" />
        </Center>
      );
    } else if (isInterviewsError) {
      return (
        <Center className="h-full">
          <ErrorText errorMessage="An error occurred while loading interviews. Please let the engineers know if they are not already on it!" />
        </Center>
      );
    }

    return (
      <Form {...form}>
        <form>
          <StickyTable
            hasRows={fields.length > 0}
            footer={
              <Row className="gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleAddInterview}
                >
                  <PlusIcon />
                  Add Interview
                </Button>
                <Button type="button" variant="ghost" onClick={handleAddScreen}>
                  <PlusIcon />
                  Add Screen
                </Button>
              </Row>
            }
          >
            <TableHeader className="sticky top-0 z-10 bg-slate-50 [&_tr]:border-b-0">
              <TableRow className="sticky-border-bottom">
                <TableHead>ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.length > 0 ? (
                fields.map((row, index) => {
                  const candidacy = interviews[index]?.candidacy;
                  const candidateName = formatting.fullName(
                    candidacy?.firstName,
                    candidacy?.lastName,
                    "",
                  );
                  const interviewType =
                    interviews[index]?.type ?? InterviewType.CUSTOMER;
                  const kind = interviewTypeKindClasses[interviewType];
                  return (
                    <TableRow
                      key={row.interviewFormId}
                      className="hover:shadow-sm"
                    >
                      <TableCell>{row.id}</TableCell>
                      <TableCell className="text-sm text-slate-700">
                        <Row className="items-center gap-2">
                          <div
                            className={cn(
                              "h-2 w-2 shrink-0 rounded-full",
                              kind.dot,
                            )}
                          />
                          {kind.label}
                        </Row>
                      </TableCell>
                      <TableCellTextInput
                        fieldName="title"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        rowId={row.id}
                      />
                      <TableCellTextInput
                        fieldName="description"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        rowId={row.id}
                      />
                      <TableCellDateTimePicker
                        fieldName="dateTime"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        rowId={row.id}
                      />
                      <TableCellEnumSelect
                        fieldName="durationMinutes"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        options={interviewDurationOptions.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        rowId={row.id}
                        type="number"
                      />
                      <TableCellEnumSelect
                        fieldName="status"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        options={interviewStatusOptions.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        rowId={row.id}
                        statusToDotColor={interviewStatusToBackgroundColor}
                      />
                      <TableCellEnumSelect
                        fieldName="timezone"
                        form={form}
                        handleOnBlur={handleOnBlur}
                        index={index}
                        listName={listName}
                        options={timezoneOptions.map((o) => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        rowId={row.id}
                      />
                      <TableCell className="text-sm text-slate-600">
                        {candidateName || <EmptyDataDash />}
                      </TableCell>
                      <TableCell>
                        <Row className="justify-center gap-4">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleDuplicateInterview(index);
                            }}
                            className="h-4 w-4"
                          >
                            <CopyPlusIcon />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleDeleteInterview(index);
                            }}
                            className="h-4 w-4"
                          >
                            <Trash2Icon />
                          </Button>
                        </Row>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell className="p-0" colSpan={10}>
                    <EmptyState
                      className="bg-slate-50 py-14"
                      description="No interviews were found. Add an interview to get started!"
                      Icon={FileIcon}
                      primaryButtonName="Add Interview"
                      PrimaryButtonIcon={PlusIcon}
                      handlePrimaryClick={handleAddInterview}
                      SecondaryButtonComponent={AddScreenButton}
                      title="No interviews/screens found"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </StickyTable>
        </form>
      </Form>
    );
  };

  return (
    <>
      {render()}
      {renderDialogs()}
    </>
  );
};
